import { useState, useMemo } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useRoute, useLocation, Link } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  insertFurnishingRoomLabelSchema,
  insertFurnishingOrderSchema,
  insertFurnishingShippingAddressSchema,
  FURNISHING_ORDER_STATUSES,
  type FurnishingCatalogItem,
  type FurnishingRoomLabel,
  type FurnishingOrder,
  type FurnishingShippingAddress,
  type FurnishingAddressBookEntry,
  type FurnishingPackage,
  type FurnishingPackageItem,
  type Property,
  type Room,
} from "@shared/schema";

type FurnishingOrderWithCreator = FurnishingOrder & { createdByName?: string | null };
type FurnishingPackageWithItems = FurnishingPackage & { items?: FurnishingPackageItem[] };

function fmtCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function parsePrice(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return isFinite(n) ? n : null;
}
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Edit, Trash2, ExternalLink, ChevronRight, Home, Package,
  MapPin, ArrowRight, Tag, CheckCircle2, Truck, Clock, ShoppingCart, Wrench, CheckSquare, BookOpen, Search
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import AddressAutocomplete from "@/components/address-autocomplete";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const ROOM_TYPE_LABELS: Record<string, string> = {
  bedroom: "Bedroom",
  bathroom: "Bathroom",
  common_space: "Common Space",
  kitchen: "Kitchen",
  appliances: "Appliances",
  exterior: "Exterior",
  other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  ordered: "Ordered",
  shipped: "Shipped",
  delivered: "Delivered",
  installed: "Installed",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  ordered: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  installed: "bg-teal-100 text-teal-800",
};

const STATUS_ICONS: Record<string, typeof Clock> = {
  pending: Clock,
  ordered: ShoppingCart,
  shipped: Truck,
  delivered: CheckCircle2,
  installed: Wrench,
};

const roomLabelFormSchema = insertFurnishingRoomLabelSchema.omit({ userId: true, propertyId: true });
type RoomLabelFormData = z.infer<typeof roomLabelFormSchema>;

const orderFormSchema = insertFurnishingOrderSchema.omit({ userId: true, propertyId: true }).extend({
  installedDate: z.string().nullable().optional(),
});
type OrderFormData = z.infer<typeof orderFormSchema>;

const addressFormSchema = insertFurnishingShippingAddressSchema.omit({ userId: true, propertyId: true });
type AddressFormData = z.infer<typeof addressFormSchema>;

export default function AppFurnishingProperty() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("furnishing");
  const [, params] = useRoute("/app/properties/:id/furnishing");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const propertyId = params?.id ? parseInt(params.id) : 0;

  const [addRoomLabelOpen, setAddRoomLabelOpen] = useState(false);
  const [editingRoomLabel, setEditingRoomLabel] = useState<FurnishingRoomLabel | null>(null);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<FurnishingOrder | null>(null);
  const [selectedRoomLabelId, setSelectedRoomLabelId] = useState<number | null>(null);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<FurnishingShippingAddress | null>(null);
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "rooms", {
    tabs: ["rooms", "addresses"],
  });
  const [saveToAddressBook, setSaveToAddressBook] = useState(false);
  const [bookNickname, setBookNickname] = useState("");
  const [addressBookSearch, setAddressBookSearch] = useState("");
  const [addressBookPickerOpen, setAddressBookPickerOpen] = useState(false);

  const [applyPackageRoomId, setApplyPackageRoomId] = useState<number | null>(null);
  const [applyPackageId, setApplyPackageId] = useState<string>("");

  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [selectedBulkRoomKey, setSelectedBulkRoomKey] = useState<string | null>(null);
  const [bulkOrderDate, setBulkOrderDate] = useState("");
  const [bulkPurchaseUrl, setBulkPurchaseUrl] = useState("");
  const [bulkTrackingUrl, setBulkTrackingUrl] = useState("");

  const propertyQuery = useQuery<Property>({
    queryKey: ["/api/app/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!propertyId,
  });
  usePageTitle(propertyQuery.data?.address || "Property Furnishing");

  const roomLabelsQuery = useQuery<FurnishingRoomLabel[]>({
    queryKey: ["/api/app/properties", propertyId, "furnishing-room-labels"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/furnishing-room-labels`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const ordersQuery = useQuery<FurnishingOrderWithCreator[]>({
    queryKey: ["/api/app/properties", propertyId, "furnishing-orders"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/furnishing-orders`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const addressesQuery = useQuery<FurnishingShippingAddress[]>({
    queryKey: ["/api/app/properties", propertyId, "furnishing-shipping-addresses"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/furnishing-shipping-addresses`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const catalogQuery = useQuery<FurnishingCatalogItem[]>({
    queryKey: ["/api/app/furnishing-catalog"],
  });

  const packagesQuery = useQuery<FurnishingPackageWithItems[]>({
    queryKey: ["/api/app/furnishing-packages"],
  });

  const property = propertyQuery.data;
  const roomLabels = roomLabelsQuery.data || [];
  const orders = ordersQuery.data || [];
  const addresses = addressesQuery.data || [];
  const catalogItems = catalogQuery.data || [];
  const packages = packagesQuery.data || [];

  // Mirrors the catalog page's estimate logic so the apply-package picker
  // shows the same "Est. $X,XXX" / "~$X,XXX · N missing price" summary
  // hosts already see on the Furnishing Catalog page (Task #1684).
  const catalogMap = useMemo(() => {
    const m: Record<number, FurnishingCatalogItem> = {};
    for (const c of catalogItems) m[c.id] = c;
    return m;
  }, [catalogItems]);

  const packageEstimates = useMemo(() => {
    const out: Record<number, { total: number; missingCount: number; hasItems: boolean }> = {};
    for (const pkg of packages) {
      const source = pkg.items || [];
      let total = 0;
      let missingCount = 0;
      for (const pi of source) {
        const cat = catalogMap[pi.catalogItemId];
        const price = parsePrice(cat?.defaultPrice);
        if (price === null) {
          missingCount += 1;
        } else {
          total += price * pi.quantity;
        }
      }
      out[pkg.id] = { total, missingCount, hasItems: source.length > 0 };
    }
    return out;
  }, [packages, catalogMap]);

  const ordersByRoomLabel = useMemo(() => {
    const map: Record<number, FurnishingOrder[]> = {};
    for (const order of orders) {
      const key = order.roomLabelId ?? 0;
      if (!map[key]) map[key] = [];
      map[key].push(order);
    }
    return map;
  }, [orders]);

  const summary = useMemo(() => {
    const total = orders.length;
    const byStatus = FURNISHING_ORDER_STATUSES.reduce((acc, s) => {
      acc[s] = orders.filter((o) => o.status === s).length;
      return acc;
    }, {} as Record<string, number>);
    return { total, byStatus };
  }, [orders]);

  const roomLabelForm = useForm<RoomLabelFormData>({
    resolver: zodResolver(roomLabelFormSchema),
    defaultValues: { roomType: "bedroom", label: "", sortOrder: 0 },
  });

  const orderForm = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: {
      itemName: "", description: null, quantity: 1, price: null,
      purchaseUrl: null, orderUrl: null, orderDate: null, status: "pending",
      roomLabelId: null, catalogItemId: null, shippingAddressId: null, notes: null,
      installedDate: null, installationExpense: null,
    },
  });

  const addressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: { label: "", addressLine1: "", addressLine2: null, city: "", state: "", zip: "", country: "US" },
  });

  const propertyRoomsQuery = useQuery<Room[]>({
    queryKey: ["/api/app/properties", propertyId, "rooms"],
    queryFn: async () => {
      const res = await fetch(`/api/app/properties/${propertyId}/rooms`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!propertyId,
  });

  const addressBookQuery = useQuery<FurnishingAddressBookEntry[]>({
    queryKey: ["/api/app/furnishing-address-book"],
  });

  const normalizeAddrKey = (a: { addressLine1: string; addressLine2?: string | null; city: string; state: string; zip: string; country?: string | null }) => {
    const norm = (s: string | null | undefined) => (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
    return [norm(a.addressLine1), norm(a.addressLine2), norm(a.city), norm(a.state), norm(a.zip), norm(a.country) || "us"].join("|");
  };

  const propertyAddressKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of addresses) set.add(normalizeAddrKey(a));
    return set;
  }, [addresses]);

  const bookEntriesNotOnProperty = useMemo(() => {
    const entries = addressBookQuery.data ?? [];
    return entries.filter((e) => !propertyAddressKeys.has(normalizeAddrKey(e)));
  }, [addressBookQuery.data, propertyAddressKeys]);

  const createAddressFromBookMutation = useMutation({
    mutationFn: async (entry: FurnishingAddressBookEntry) => {
      const payload = {
        label: entry.label,
        addressLine1: entry.addressLine1,
        addressLine2: entry.addressLine2 ?? null,
        city: entry.city,
        state: entry.state,
        zip: entry.zip,
        country: entry.country || "US",
      };
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/furnishing-shipping-addresses`, payload);
      return res.json() as Promise<FurnishingShippingAddress>;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-shipping-addresses"] });
      orderForm.setValue("shippingAddressId", created.id, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      toast({ title: "Address added to property", description: `"${created.label}" is now available for orders here.` });
    },
    onError: (err) => toast({ title: "Could not add address from book", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const handleShippingAddressSelect = (raw: string) => {
    if (raw === "none") {
      orderForm.setValue("shippingAddressId", null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
      return;
    }
    if (raw.startsWith("book:")) {
      const bookId = parseInt(raw.slice("book:".length));
      const entry = bookEntriesNotOnProperty.find((e) => e.id === bookId);
      if (entry) createAddressFromBookMutation.mutate(entry);
      return;
    }
    orderForm.setValue("shippingAddressId", parseInt(raw), { shouldDirty: true, shouldValidate: true, shouldTouch: true });
  };

  const createRoomLabelMutation = useMutation({
    mutationFn: async (data: RoomLabelFormData) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/furnishing-room-labels`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-room-labels"] });
      setAddRoomLabelOpen(false);
      roomLabelForm.reset();
      toast({ title: "Room added" });
    },
    onError: (err) => toast({ title: "Failed to add room", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  function padNum(n: number): string {
    return n < 10 ? `0${n}` : String(n);
  }

  const autoGenerateRoomsMutation = useMutation({
    mutationFn: async () => {
      const propertyRooms = propertyRoomsQuery.data ?? [];
      const existingLabels = roomLabels;
      const existingNamesLower = new Set(existingLabels.map((l) => l.label.trim().toLowerCase()));

      const bedrooms = propertyRooms.filter((r) => r.roomType === "bedroom" && r.shadowOfRoomId == null);
      const bathrooms = propertyRooms.filter((r) => r.roomType === "bathroom");

      type SlotDef = { label: string; roomType: string; sortOrder: number };
      const desired: SlotDef[] = [];

      bedrooms.forEach((_, i) => {
        desired.push({ label: `Bedroom ${padNum(i + 1)}`, roomType: "bedroom", sortOrder: i });
      });
      bathrooms.forEach((_, i) => {
        desired.push({ label: `Bathroom ${padNum(i + 1)}`, roomType: "bathroom", sortOrder: bedrooms.length + i });
      });
      desired.push({ label: "Common Space", roomType: "common_space", sortOrder: bedrooms.length + bathrooms.length });
      desired.push({ label: "Exterior", roomType: "exterior", sortOrder: bedrooms.length + bathrooms.length + 1 });

      const toCreate = desired.filter((d) => !existingNamesLower.has(d.label.trim().toLowerCase()));

      for (const slot of toCreate) {
        await apiRequest("POST", `/api/app/properties/${propertyId}/furnishing-room-labels`, slot);
      }
      return toCreate.length;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-room-labels"] });
      if (created === 0) {
        toast({ title: "All rooms already present", description: "No new slots were needed." });
      } else {
        toast({ title: `${created} room slot${created === 1 ? "" : "s"} created` });
      }
    },
    onError: (err) => toast({ title: "Failed to auto-generate rooms", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updateRoomLabelMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<RoomLabelFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-room-labels/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-room-labels"] });
      setEditingRoomLabel(null);
      roomLabelForm.reset();
      toast({ title: "Room updated" });
    },
    onError: (err) => toast({ title: "Failed to update room", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const deleteRoomLabelMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-room-labels/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-room-labels"] });
      toast({ title: "Room removed" });
    },
    onError: (err) => toast({ title: "Failed to remove room", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const createOrderMutation = useMutation({
    mutationFn: async (data: OrderFormData) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/furnishing-orders`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-orders"] });
      setAddOrderOpen(false);
      orderForm.reset();
      toast({ title: "Order added" });
    },
    onError: (err) => toast({ title: "Failed to add order", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<OrderFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-orders/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-orders"] });
      setEditingOrder(null);
      orderForm.reset();
      toast({ title: "Order updated" });
    },
    onError: (err) => toast({ title: "Failed to update order", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const deleteOrderMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-orders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-orders"] });
      toast({ title: "Order removed" });
    },
    onError: (err) => toast({ title: "Failed to remove order", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const saveToBookMutation = useMutation({
    mutationFn: async (data: { nickname: string; label: string; addressLine1: string; addressLine2?: string | null; city: string; state: string; zip: string; country: string }) => {
      const existingEntry = addressBookQuery.data?.find((e) => e.nickname === data.nickname);
      if (existingEntry) {
        const res = await apiRequest("PATCH", `/api/app/furnishing-address-book/${existingEntry.id}`, data);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/app/furnishing-address-book", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-address-book"] });
    },
    onError: (err) => toast({ title: "Could not save to address book", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const createAddressMutation = useMutation({
    mutationFn: async (data: AddressFormData) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/furnishing-shipping-addresses`, data);
      return res.json();
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-shipping-addresses"] });
      if (saveToAddressBook && bookNickname.trim()) {
        await saveToBookMutation.mutateAsync({
          nickname: bookNickname.trim(),
          label: variables.label,
          addressLine1: variables.addressLine1,
          addressLine2: variables.addressLine2,
          city: variables.city,
          state: variables.state,
          zip: variables.zip,
          country: variables.country ?? "US",
        });
      }
      setAddAddressOpen(false);
      setSaveToAddressBook(false);
      setBookNickname("");
      addressForm.reset();
      toast({ title: "Shipping address saved" });
    },
    onError: (err) => toast({ title: "Failed to save address", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updateAddressMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<AddressFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-shipping-addresses/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-shipping-addresses"] });
      setEditingAddress(null);
      addressForm.reset();
      toast({ title: "Address updated" });
    },
    onError: (err) => toast({ title: "Failed to update address", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const deleteAddressMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-shipping-addresses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-shipping-addresses"] });
      toast({ title: "Address removed" });
    },
    onError: (err) => toast({ title: "Failed to remove address", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const applyPackageMutation = useMutation({
    mutationFn: async ({ packageId, roomLabelId }: { packageId: number; roomLabelId: number | null }) => {
      const res = await apiRequest("POST", `/api/app/properties/${propertyId}/apply-furnishing-package`, { packageId, roomLabelId });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-orders"] });
      setApplyPackageRoomId(null);
      setApplyPackageId("");
      toast({ title: `${data.length} item(s) added from package` });
    },
    onError: (err) => toast({ title: "Failed to apply package", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ ids, orderDate, purchaseUrl, trackingUrl }: { ids: number[]; orderDate?: string; purchaseUrl?: string; trackingUrl?: string }) => {
      const payload: Record<string, unknown> = { ids };
      if (orderDate) payload.orderDate = orderDate;
      if (purchaseUrl) payload.purchaseUrl = purchaseUrl;
      if (trackingUrl) payload.trackingUrl = trackingUrl;
      const res = await apiRequest("PATCH", "/api/app/furnishing-orders/bulk-update", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties", propertyId, "furnishing-orders"] });
      setSelectedOrderIds(new Set());
      setSelectedBulkRoomKey(null);
      setBulkOrderDate("");
      setBulkPurchaseUrl("");
      setBulkTrackingUrl("");
      toast({ title: "Bulk update applied" });
    },
    onError: (err) => toast({ title: "Failed to bulk update", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const openAddOrder = (roomLabelId: number | null = null) => {
    setEditingOrder(null);
    orderForm.reset({
      itemName: "", description: null, quantity: 1, price: null,
      purchaseUrl: null, orderUrl: null, orderDate: null, status: "pending",
      roomLabelId, catalogItemId: null, shippingAddressId: null, notes: null,
      installedDate: null, installationExpense: null,
    });
    setSelectedRoomLabelId(roomLabelId);
    setAddOrderOpen(true);
  };

  const openEditOrder = (order: FurnishingOrder) => {
    setEditingOrder(order);
    orderForm.reset({
      itemName: order.itemName,
      description: order.description ?? null,
      quantity: order.quantity,
      price: order.price ?? null,
      purchaseUrl: order.purchaseUrl ?? null,
      orderUrl: order.orderUrl ?? null,
      orderDate: order.orderDate ?? null,
      status: order.status as typeof FURNISHING_ORDER_STATUSES[number],
      roomLabelId: order.roomLabelId ?? null,
      catalogItemId: order.catalogItemId ?? null,
      shippingAddressId: order.shippingAddressId ?? null,
      notes: order.notes ?? null,
      installedDate: order.installedDate ? new Date(order.installedDate).toISOString().split("T")[0] : null,
      installationExpense: order.installationExpense ?? null,
    });
    setAddOrderOpen(true);
  };

  const openEditAddress = (addr: FurnishingShippingAddress) => {
    setEditingAddress(addr);
    setSaveToAddressBook(false);
    setBookNickname("");
    setAddressBookSearch("");
    addressForm.reset({
      label: addr.label,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2 ?? null,
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
      country: addr.country ?? "US",
    });
    setAddAddressOpen(true);
  };

  const onSubmitRoomLabel = (data: RoomLabelFormData) => {
    if (editingRoomLabel) {
      updateRoomLabelMutation.mutate({ id: editingRoomLabel.id, data });
    } else {
      createRoomLabelMutation.mutate(data);
    }
  };

  const onSubmitOrder = (data: OrderFormData) => {
    const payload = { ...data };
    if (payload.status !== "installed") {
      payload.installedDate = null;
      payload.installationExpense = null;
    }
    if (editingOrder) {
      updateOrderMutation.mutate({ id: editingOrder.id, data: payload });
    } else {
      createOrderMutation.mutate(payload);
    }
  };

  const onSubmitAddress = (data: AddressFormData) => {
    if (editingAddress) {
      updateAddressMutation.mutate({ id: editingAddress.id, data });
    } else {
      createAddressMutation.mutate(data);
    }
  };

  const onCatalogItemSelect = (catalogItemId: string) => {
    const id = parseInt(catalogItemId);
    const item = catalogItems.find((c) => c.id === id);
    if (item) {
      orderForm.setValue("catalogItemId", id);
      orderForm.setValue("itemName", item.name);
      if (item.defaultPrice) orderForm.setValue("price", item.defaultPrice);
      if (item.purchaseUrl) orderForm.setValue("purchaseUrl", item.purchaseUrl);
      if (item.description) orderForm.setValue("description", item.description);
    }
  };

  const updateOrderStatus = (orderId: number, status: string) => {
    const payload: Partial<OrderFormData> = { status: status as typeof FURNISHING_ORDER_STATUSES[number] };
    if (status === "installed") payload.installedDate = new Date().toISOString().split("T")[0];
    updateOrderMutation.mutate({ id: orderId, data: payload });
  };

  const toggleOrderSelection = (id: number, roomKey: string) => {
    if (selectedBulkRoomKey !== null && selectedBulkRoomKey !== roomKey && selectedOrderIds.size > 0) {
      setSelectedOrderIds(new Set([id]));
      setSelectedBulkRoomKey(roomKey);
      return;
    }
    setSelectedBulkRoomKey(roomKey);
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      if (next.size === 0) setSelectedBulkRoomKey(null);
      return next;
    });
  };

  const watchedStatus = orderForm.watch("status");

  if (propertyQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!property) {
    return (
      <AppLayout>
        <div className="p-6 text-center py-20">
          <p className="text-gray-500">Property not found</p>
        </div>
      </AppLayout>
    );
  }

  const unassignedOrders = ordersByRoomLabel[0] || [];

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="furnishing" />
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link href="/app/properties">
            <span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-properties">Properties</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link href={`/app/properties/${propertyId}`}>
            <span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-property">{property.name}</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-900">Furnishing</span>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-furnishing-title" data-tour="furnishing-property-heading">
              Furnishing — {property.name}
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage per-room furnishing orders for this property</p>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/app/furnishing-catalog">
              <Button variant="outline" size="sm" data-testid="link-open-catalog">
                <Package className="h-4 w-4 mr-1.5" />
                View Catalog
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="furnishing-property" />
              <GuideButton pageKey="furnishing-property" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="furnishing-property" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {FURNISHING_ORDER_STATUSES.map((status) => {
            const Icon = STATUS_ICONS[status];
            const count = summary.byStatus[status] || 0;
            return (
              <div key={status} className="border rounded-lg p-4" data-testid={`stat-${status}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-medium text-gray-500">{STATUS_LABELS[status]}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{count}</p>
              </div>
            );
          })}
        </div>

        {selectedOrderIds.size > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap items-end gap-3" data-testid="bulk-action-toolbar">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
              <CheckSquare className="h-4 w-4" />
              {selectedOrderIds.size} item(s) selected in this room
            </div>
            <p className="w-full text-xs text-blue-600 -mt-1">Fill only the fields you want to update — blank fields will be skipped. Selections are scoped to one room at a time.</p>
            <div className="flex-1 flex flex-wrap items-end gap-2">
              <div>
                <label className="text-xs text-gray-600 block mb-1">Order Date</label>
                <Input type="date" value={bulkOrderDate} onChange={(e) => setBulkOrderDate(e.target.value)} className="h-8 text-sm w-40" data-testid="input-bulk-order-date" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Purchase URL</label>
                <Input value={bulkPurchaseUrl} onChange={(e) => setBulkPurchaseUrl(e.target.value)} placeholder="https://..." className="h-8 text-sm w-56" data-testid="input-bulk-purchase-url" />
              </div>
              <div>
                <label className="text-xs text-gray-600 block mb-1">Tracking URL</label>
                <Input value={bulkTrackingUrl} onChange={(e) => setBulkTrackingUrl(e.target.value)} placeholder="https://..." className="h-8 text-sm w-56" data-testid="input-bulk-tracking-url" />
              </div>
              <Button size="sm" disabled={readOnly || (bulkUpdateMutation.isPending || (!bulkOrderDate && !bulkPurchaseUrl && !bulkTrackingUrl))} onClick={() => bulkUpdateMutation.mutate({ ids: Array.from(selectedOrderIds), orderDate: bulkOrderDate || undefined, purchaseUrl: bulkPurchaseUrl || undefined, trackingUrl: bulkTrackingUrl || undefined })} data-testid="button-bulk-apply">
                Apply to {selectedOrderIds.size} Item{selectedOrderIds.size !== 1 ? "s" : ""}
              </Button>
              <Button disabled={readOnly} size="sm" variant="outline" onClick={() => { setSelectedOrderIds(new Set()); setSelectedBulkRoomKey(null); setBulkOrderDate(""); setBulkPurchaseUrl(""); setBulkTrackingUrl(""); }} data-testid="button-bulk-cancel">
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-4 border-b mb-6">
          <button
            className={`pb-3 text-sm font-medium border-b-2 ${activeTab === "rooms" ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("rooms")}
            data-testid="tab-furnishing-rooms"
          >
            Rooms & Orders ({orders.length})
          </button>
          <button
            className={`pb-3 text-sm font-medium border-b-2 ${activeTab === "addresses" ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            onClick={() => setActiveTab("addresses")}
            data-testid="tab-furnishing-addresses"
          >
            Shipping Addresses ({addresses.length})
          </button>
        </div>

        {activeTab === "rooms" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Room Order Lists</h2>
              <div className="flex gap-2">
                <Button disabled={readOnly || autoGenerateRoomsMutation.isPending || !propertyRoomsQuery.isSuccess} size="sm" variant="outline" onClick={() => autoGenerateRoomsMutation.mutate()} data-testid="button-auto-generate-rooms">
                  {autoGenerateRoomsMutation.isPending ? "Generating…" : "Auto-generate rooms"}
                </Button>
                <Button disabled={readOnly} size="sm" variant="outline" onClick={() => { setEditingRoomLabel(null); roomLabelForm.reset(); setAddRoomLabelOpen(true); }} data-testid="button-add-room-label">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Room
                </Button>
                <Button disabled={readOnly} size="sm" onClick={() => openAddOrder(null)} data-testid="button-add-order" data-tour="furnishing-property-add">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Order Item
                </Button>
              </div>
            </div>

            {roomLabels.length === 0 && unassignedOrders.length === 0 ? (
              <div className="text-center py-16 border rounded-lg">
                <Home className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No rooms set up yet</p>
                <p className="text-sm text-gray-400 mt-1">Auto-generate slots from your property's rooms, or add them manually</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
                  <Button disabled={readOnly || autoGenerateRoomsMutation.isPending || !propertyRoomsQuery.isSuccess} onClick={() => autoGenerateRoomsMutation.mutate()} data-testid="button-auto-generate-rooms-empty">
                    {autoGenerateRoomsMutation.isPending ? "Generating…" : "Auto-generate rooms"}
                  </Button>
                  <Button disabled={readOnly} variant="outline" onClick={() => { setEditingRoomLabel(null); roomLabelForm.reset(); setAddRoomLabelOpen(true); }} data-testid="button-add-first-room">
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add Room Manually
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4" data-tour="furnishing-property-rooms">
                {roomLabels.map((room) => {
                  const roomOrders = ordersByRoomLabel[room.id] || [];
                  const delivered = roomOrders.filter((o) => o.status === "delivered" || o.status === "installed").length;
                  return (
                    <div key={room.id} className="border rounded-lg overflow-hidden" data-testid={`room-section-${room.id}`}>
                      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
                        <div className="flex items-center gap-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{room.label}</h3>
                            <span className="text-xs text-gray-500">{ROOM_TYPE_LABELS[room.roomType] || room.roomType}</span>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {delivered}/{roomOrders.length} delivered/installed
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {packages.length > 0 && (
                            applyPackageRoomId === room.id ? (
                              <div className="flex items-center gap-2">
                                <Select value={applyPackageId} onValueChange={setApplyPackageId}>
                                  <SelectTrigger className="h-7 text-xs w-64" data-testid={`select-package-for-room-${room.id}`}>
                                    <SelectValue placeholder="Select package..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {packages.filter((p) => p.roomType === room.roomType).length === 0 ? (
                                      <SelectItem value="__none__" disabled>No packages for this room type</SelectItem>
                                    ) : (
                                      packages.filter((p) => p.roomType === room.roomType).map((p) => {
                                        const est = packageEstimates[p.id];
                                        let estLabel: string | null = null;
                                        let estClass = "text-gray-400";
                                        if (est) {
                                          if (!est.hasItems) {
                                            estLabel = "No items";
                                          } else if (est.missingCount > 0) {
                                            estLabel = `~${fmtCurrency(est.total)} · ${est.missingCount} ${est.missingCount === 1 ? "item" : "items"} missing price`;
                                            estClass = "text-gray-500";
                                          } else {
                                            estLabel = `Est. ${fmtCurrency(est.total)}`;
                                            estClass = "text-blue-700 font-medium";
                                          }
                                        }
                                        return (
                                          <SelectItem key={p.id} value={String(p.id)} data-testid={`select-package-option-${p.id}`}>
                                            <span className="flex items-center justify-between gap-3 w-full">
                                              <span>{p.name}</span>
                                              {estLabel && (
                                                <span
                                                  className={`text-xs ${estClass}`}
                                                  data-testid={`text-package-option-estimate-${p.id}`}
                                                >
                                                  {estLabel}
                                                </span>
                                              )}
                                            </span>
                                          </SelectItem>
                                        );
                                      })
                                    )}
                                  </SelectContent>
                                </Select>
                                <Button size="sm" variant="default" disabled={readOnly || (!applyPackageId || applyPackageMutation.isPending)} onClick={() => applyPackageMutation.mutate({ packageId: parseInt(applyPackageId), roomLabelId: room.id })} data-testid={`button-confirm-apply-package-${room.id}`}>
                                  Apply
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setApplyPackageRoomId(null); setApplyPackageId(""); }} data-testid={`button-cancel-apply-package-${room.id}`}>
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button disabled={readOnly} size="sm" variant="ghost" onClick={() => { setApplyPackageRoomId(room.id); setApplyPackageId(""); }} data-testid={`button-apply-package-${room.id}`}>
                                <Package className="h-4 w-4 mr-1" />
                                Apply Package
                              </Button>
                            )
                          )}
                          <Button disabled={readOnly} size="sm" variant="ghost" onClick={() => openAddOrder(room.id)} data-testid={`button-add-order-room-${room.id}`}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add Item
                          </Button>
                          <Button disabled={readOnly} size="sm" variant="ghost" onClick={() => { setEditingRoomLabel(room); roomLabelForm.reset({ roomType: room.roomType as "bedroom", label: room.label, sortOrder: room.sortOrder ?? 0 }); setAddRoomLabelOpen(true); }} data-testid={`button-edit-room-${room.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button disabled={readOnly} size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteRoomLabelMutation.mutate(room.id)} data-testid={`button-delete-room-${room.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {roomOrders.length === 0 ? (
                        <div className="p-6 text-center text-gray-400 text-sm">No items ordered for this room yet</div>
                      ) : (
                        <OrderTable
                          orders={roomOrders}
                          addresses={addresses}
                          onEdit={openEditOrder}
                          onDelete={(id) => deleteOrderMutation.mutate(id)}
                          onStatusChange={updateOrderStatus}
                          selectedIds={selectedOrderIds}
                          bulkRoomKey={selectedBulkRoomKey}
                          roomKey={`room-${room.id}`}
                          onSelectionChange={(next) => {
                            // next is room-scoped (filtered inside OrderTable)
                            setSelectedBulkRoomKey(next.size > 0 ? `room-${room.id}` : null);
                            setSelectedOrderIds(next);
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                {unassignedOrders.length > 0 && (
                  <div className="border rounded-lg overflow-hidden" data-testid="room-section-unassigned">
                    <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b" data-tour="furnishing-property-orders">
                      <h3 className="font-semibold text-gray-900 text-sm">Unassigned Items</h3>
                    </div>
                    <OrderTable
                      orders={unassignedOrders}
                      addresses={addresses}
                      onEdit={openEditOrder}
                      onDelete={(id) => deleteOrderMutation.mutate(id)}
                      onStatusChange={updateOrderStatus}
                      selectedIds={selectedOrderIds}
                      bulkRoomKey={selectedBulkRoomKey}
                      roomKey="room-unassigned"
                      onSelectionChange={(next) => {
                        // next is room-scoped (filtered inside OrderTable)
                        setSelectedBulkRoomKey(next.size > 0 ? "room-unassigned" : null);
                        setSelectedOrderIds(next);
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "addresses" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Shipping Addresses</h2>
              <div className="flex gap-2">
                <Link href="/app/furnishing-address-book">
                  <Button variant="outline" size="sm" data-testid="button-manage-address-book">
                    <BookOpen className="h-4 w-4 mr-1.5" />
                    Manage Address Book
                  </Button>
                </Link>
                <Button disabled={readOnly} size="sm" onClick={() => { setEditingAddress(null); setSaveToAddressBook(false); setBookNickname(""); setAddressBookSearch(""); addressForm.reset(); setAddAddressOpen(true); }} data-testid="button-add-address">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Address
                </Button>
              </div>
            </div>

            {addresses.length === 0 ? (
              <div className="text-center py-16 border rounded-lg">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No shipping addresses yet</p>
                <p className="text-sm text-gray-400 mt-1">Add labeled addresses to identify where shipments should go</p>
                <Button disabled={readOnly} className="mt-4" onClick={() => { setEditingAddress(null); setSaveToAddressBook(false); setBookNickname(""); setAddressBookSearch(""); addressForm.reset(); setAddAddressOpen(true); }} data-testid="button-add-first-address">
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add First Address
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {addresses.map((addr) => (
                  <div key={addr.id} className="border rounded-lg p-4" data-testid={`card-address-${addr.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-400" />
                          <span className="font-semibold text-gray-900 text-sm" data-testid={`text-address-label-${addr.id}`}>{addr.label}</span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button disabled={readOnly} size="sm" variant="ghost" onClick={() => openEditAddress(addr)} data-testid={`button-edit-address-${addr.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button disabled={readOnly} size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteAddressMutation.mutate(addr.id)} data-testid={`button-delete-address-${addr.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 ml-6">
                      <p>{addr.addressLine1}</p>
                      {addr.addressLine2 && <p>{addr.addressLine2}</p>}
                      <p>{addr.city}, {addr.state} {addr.zip}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Dialog open={addRoomLabelOpen} onOpenChange={(open) => { setAddRoomLabelOpen(open); if (!open) setEditingRoomLabel(null); }}>
          <DialogContent className="max-w-md" data-testid="dialog-room-label">
            <DialogHeader>
              <DialogTitle>{editingRoomLabel ? "Edit Room" : "Add Room"}</DialogTitle>
            </DialogHeader>
            <Form {...roomLabelForm}>
              <form onSubmit={roomLabelForm.handleSubmit(onSubmitRoomLabel)} className="space-y-4">
                <FormField control={roomLabelForm.control} name="label" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Room Label *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g., "Bedroom 1" or "Master Bedroom"' data-testid="input-room-label" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={roomLabelForm.control} name="roomType" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Room Type</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger data-testid="select-room-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {["bedroom", "bathroom", "common_space", "kitchen", "appliances", "exterior", "other"].map((rt) => (
                            <SelectItem key={rt} value={rt}>{ROOM_TYPE_LABELS[rt]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAddRoomLabelOpen(false)} data-testid="button-cancel-room">Cancel</Button>
                  <Button type="submit" disabled={readOnly || (createRoomLabelMutation.isPending || updateRoomLabelMutation.isPending)} data-testid="button-save-room">
                    {editingRoomLabel ? "Save" : "Add Room"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <Dialog open={addOrderOpen} onOpenChange={(open) => { setAddOrderOpen(open); if (!open) setEditingOrder(null); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-furnishing-order">
            <DialogHeader>
              <DialogTitle>{editingOrder ? "Edit Order Item" : "Add Order Item"}</DialogTitle>
            </DialogHeader>
            <Form {...orderForm}>
              <form onSubmit={orderForm.handleSubmit(onSubmitOrder)} className="space-y-4">
                {catalogItems.length > 0 && !editingOrder && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">From Catalog (optional)</label>
                    <Select onValueChange={onCatalogItemSelect}>
                      <SelectTrigger data-testid="select-catalog-item">
                        <SelectValue placeholder="Choose from catalog..." />
                      </SelectTrigger>
                      <SelectContent>
                        {catalogItems.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-1">Select to pre-fill fields, or fill in manually below</p>
                  </div>
                )}

                <FormField control={orderForm.control} name="itemName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g., Queen Bed Frame" data-testid="input-order-item-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={orderForm.control} name="quantity" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quantity</FormLabel>
                      <FormControl>
                        <Input type="number" min={1} {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} data-testid="input-order-quantity" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={orderForm.control} name="price" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price ($)</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ""} placeholder="e.g., 149.99" data-testid="input-order-price" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={orderForm.control} name="roomLabelId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Room</FormLabel>
                    <FormControl>
                      <Select value={field.value ? String(field.value) : "none"} onValueChange={(v) => field.onChange(v === "none" ? null : parseInt(v))}>
                        <SelectTrigger data-testid="select-order-room">
                          <SelectValue placeholder="Assign to room..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No room assigned</SelectItem>
                          {roomLabels.map((r) => (
                            <SelectItem key={r.id} value={String(r.id)}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={orderForm.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger data-testid="select-order-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FURNISHING_ORDER_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                {watchedStatus === "installed" && (
                  <div className="grid grid-cols-2 gap-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <FormField control={orderForm.control} name="installedDate" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Installed Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} value={field.value ?? ""} data-testid="input-installed-date" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={orderForm.control} name="installationExpense" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Installation Cost ($)</FormLabel>
                        <FormControl>
                          <Input {...field} value={field.value ?? ""} placeholder="e.g., 150.00" data-testid="input-installation-expense" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                )}

                <FormField control={orderForm.control} name="shippingAddressId" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shipping Address</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value ? String(field.value) : "none"}
                        onValueChange={handleShippingAddressSelect}
                        disabled={createAddressFromBookMutation.isPending}
                      >
                        <SelectTrigger data-testid="select-order-shipping-address">
                          <SelectValue placeholder={createAddressFromBookMutation.isPending ? "Adding from address book..." : "Select address..."} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No address selected</SelectItem>
                          {addresses.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>This property</SelectLabel>
                              {addresses.map((a) => (
                                <SelectItem key={a.id} value={String(a.id)} data-testid={`select-shipping-address-option-${a.id}`}>
                                  {a.label} · {a.addressLine1}, {a.city}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {bookEntriesNotOnProperty.length > 0 && (
                            <>
                              {addresses.length > 0 && <SelectSeparator />}
                              <SelectGroup>
                                <SelectLabel>From your address book</SelectLabel>
                                {bookEntriesNotOnProperty.map((e) => (
                                  <SelectItem key={`book-${e.id}`} value={`book:${e.id}`} data-testid={`select-shipping-address-book-option-${e.id}`}>
                                    {e.nickname} · {e.label} — {e.addressLine1}, {e.city}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {bookEntriesNotOnProperty.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">Picking an address book entry adds it to this property automatically.</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={orderForm.control} name="orderDate" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ""} data-testid="input-order-date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={orderForm.control} name="purchaseUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase URL</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} placeholder="https://..." data-testid="input-order-purchase-url" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={orderForm.control} name="orderUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Order / Tracking URL</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} placeholder="https://amazon.com/orders/..." data-testid="input-order-tracking-url" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={orderForm.control} name="notes" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value ?? ""} rows={2} placeholder="Optional notes..." data-testid="textarea-order-notes" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setAddOrderOpen(false)} data-testid="button-cancel-order">Cancel</Button>
                  <Button type="submit" disabled={readOnly || (createOrderMutation.isPending || updateOrderMutation.isPending)} data-testid="button-save-order">
                    {editingOrder ? "Save Changes" : "Add Item"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <Dialog open={addAddressOpen} onOpenChange={(open) => { setAddAddressOpen(open); if (!open) { setEditingAddress(null); setSaveToAddressBook(false); setBookNickname(""); setAddressBookSearch(""); setAddressBookPickerOpen(false); } }}>
          <DialogContent
            className="max-w-md max-h-[90vh] flex flex-col overflow-hidden"
            data-testid="dialog-shipping-address"
            onInteractOutside={(e) => {
              const target = e.target as HTMLElement | null;
              if (target?.closest?.('[data-address-autocomplete-portal]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingAddress ? "Edit Address" : "Add Shipping Address"}</DialogTitle>
            </DialogHeader>
            <Form {...addressForm}>
              <form onSubmit={addressForm.handleSubmit(onSubmitAddress)} className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
                {!editingAddress && (addressBookQuery.data?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-1">Pick from address book</p>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                      <Input
                        value={addressBookSearch}
                        onChange={(e) => { setAddressBookSearch(e.target.value); setAddressBookPickerOpen(true); }}
                        onFocus={() => setAddressBookPickerOpen(true)}
                        placeholder="Search by nickname, label, street, or city..."
                        className="pl-8 text-sm"
                        data-testid="input-address-book-search"
                      />
                    </div>
                    {addressBookPickerOpen && (
                      <div className="border rounded-md mt-1 bg-white shadow-sm max-h-48 overflow-auto">
                        {(addressBookQuery.data ?? [])
                          .filter((e) => {
                            if (!addressBookSearch.trim()) return true;
                            const q = addressBookSearch.toLowerCase();
                            return String(e.nickname ?? "").toLowerCase().includes(q) || String(e.label ?? "").toLowerCase().includes(q) || String(e.addressLine1 ?? "").toLowerCase().includes(q) || String(e.city ?? "").toLowerCase().includes(q);
                          })
                          .map((entry) => (
                            <button
                              key={entry.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              data-testid={`address-book-option-${entry.id}`}
                              onClick={() => {
                                addressForm.setValue("label", entry.label, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("addressLine1", entry.addressLine1, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("addressLine2", entry.addressLine2 ?? null, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("city", entry.city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("state", entry.state, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("zip", entry.zip, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                addressForm.setValue("country", entry.country || "US", { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                                setAddressBookSearch(entry.nickname);
                                setAddressBookPickerOpen(false);
                              }}
                            >
                              <span className="font-medium">{entry.nickname}</span>
                              <span className="text-gray-500 ml-2 text-xs">{entry.label} · {entry.addressLine1}, {entry.city}</span>
                            </button>
                          ))}
                        {(addressBookQuery.data ?? []).filter((e) => {
                          if (!addressBookSearch.trim()) return true;
                          const q = addressBookSearch.toLowerCase();
                          return String(e.nickname ?? "").toLowerCase().includes(q) || String(e.label ?? "").toLowerCase().includes(q) || String(e.addressLine1 ?? "").toLowerCase().includes(q) || String(e.city ?? "").toLowerCase().includes(q);
                        }).length === 0 && (
                          <p className="px-3 py-2 text-xs text-gray-400">No matches found</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <FormField control={addressForm.control} name="label" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient name / label *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g., Example — Unit A Room 1' data-testid="input-address-label" />
                    </FormControl>
                    <FormDescription className="text-xs text-gray-500">
                      This becomes line 1 of the shipping label. Encode the room route so installers can sort packages — e.g. <em>Example — Unit A Room 1</em> or <em>Example — Common Area</em>.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={addressForm.control} name="addressLine1" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 1 *</FormLabel>
                    <FormControl>
                      <AddressAutocomplete
                        value={field.value}
                        placeholder="123 Main St"
                        data-testid="input-address-line1"
                        onSelect={(address, _coords, zipCode, city, state, streetAddress, country) => {
                          field.onChange(streetAddress || address);
                          if (city) addressForm.setValue("city", city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          if (state) addressForm.setValue("state", state, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          if (zipCode) addressForm.setValue("zip", zipCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          addressForm.setValue("country", country || "US", { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                        }}
                        onChange={(val) => field.onChange(val)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={addressForm.control} name="addressLine2" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 2</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} placeholder="Apt, Suite, etc." data-testid="input-address-line2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-3 gap-3">
                  <FormField control={addressForm.control} name="city" render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Atlanta" data-testid="input-address-city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={addressForm.control} name="state" render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="GA" data-testid="input-address-state" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={addressForm.control} name="zip" render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="30301" data-testid="input-address-zip" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={addressForm.control} name="country" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="US" data-testid="input-address-country" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                {!editingAddress && (
                  <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="save-to-book"
                        checked={saveToAddressBook}
                        onCheckedChange={(v) => setSaveToAddressBook(!!v)}
                        data-testid="checkbox-save-to-address-book"
                      />
                      <label htmlFor="save-to-book" className="text-sm font-medium text-gray-700 cursor-pointer">Save to my address book</label>
                    </div>
                    {saveToAddressBook && (
                      <Input
                        value={bookNickname}
                        onChange={(e) => setBookNickname(e.target.value)}
                        placeholder='Nickname, e.g. "Tiago staging" or "Anna property"'
                        className="text-sm"
                        data-testid="input-address-book-nickname"
                      />
                    )}
                  </div>
                )}
                </div>
                <div className="flex-shrink-0 flex justify-end gap-2 pt-4 border-t mt-2">
                  <Button type="button" variant="outline" onClick={() => setAddAddressOpen(false)} data-testid="button-cancel-address">Cancel</Button>
                  <Button type="submit" disabled={readOnly || (createAddressMutation.isPending || updateAddressMutation.isPending)} data-testid="button-save-address">
                    {editingAddress ? "Save" : "Add Address"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

function OrderTable({ orders, addresses, onEdit, onDelete, onStatusChange, selectedIds, onSelectionChange, roomKey, bulkRoomKey }: {
  orders: FurnishingOrderWithCreator[];
  addresses: FurnishingShippingAddress[];
  onEdit: (order: FurnishingOrder) => void;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void;
  selectedIds: Set<number>;
  onSelectionChange: (next: Set<number>) => void;
  roomKey: string;
  bulkRoomKey: string | null;
}) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("furnishing");
  const isRoomLockedOut = bulkRoomKey !== null && bulkRoomKey !== roomKey;
  const addressMap = useMemo(() => {
    const m: Record<number, FurnishingShippingAddress> = {};
    for (const a of addresses) m[a.id] = a;
    return m;
  }, [addresses]);
  // Scope selection to this room only before passing to the hook so that
  // cross-room IDs never contaminate range computation or the next Set.
  const roomOrderIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const roomOrderIdSet = useMemo(() => new Set(roomOrderIds), [roomOrderIds]);
  const roomSelectedIds = useMemo(
    () => new Set([...selectedIds].filter((id) => roomOrderIdSet.has(id))),
    [selectedIds, roomOrderIdSet],
  );
  const { handleItemClick: handleOrderClick } = useRangeSelection(
    roomOrderIds,
    roomSelectedIds,
    onSelectionChange,
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-white border-b">
          <tr>
            <th className="px-4 py-2 w-8"></th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Item</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Qty</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Price</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Shipping To</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Links</th>
            <th className="text-left px-4 py-2 font-medium text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const addr = order.shippingAddressId ? addressMap[order.shippingAddressId] : null;
            const isSelected = selectedIds.has(order.id);
            return (
              <tr key={order.id} className={`border-b last:border-0 hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`} data-testid={`row-order-${order.id}`}>
                <td className="px-4 py-3" onClick={(e) => { e.stopPropagation(); if (!isRoomLockedOut) handleOrderClick(order.id, e.shiftKey); }}>
                  <Checkbox
                    checked={isSelected}
                    disabled={isRoomLockedOut}
                    onCheckedChange={() => {}}
                    data-testid={`checkbox-order-${order.id}`}
                    className={isRoomLockedOut ? "opacity-30" : ""}
                  />
                </td>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {order.itemName}
                  {order.notes && <p className="text-xs text-gray-400 font-normal">{order.notes}</p>}
                  <p className="text-xs text-gray-400 font-normal" data-testid={`text-order-added-by-${order.id}`}>
                    Added by {order.createdByName || "Unknown"}
                  </p>
                  {order.status === "installed" && order.installedDate && (
                    <p className="text-xs text-teal-600 font-normal">
                      Installed: {new Date(order.installedDate).toLocaleDateString()}
                      {order.installationExpense && ` · $${order.installationExpense}`}
                    </p>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-700">{order.quantity}</td>
                <td className="px-4 py-3 text-gray-700">{order.price ? `$${order.price}` : "—"}</td>
                <td className="px-4 py-3">
                  <Select value={order.status} onValueChange={(v) => onStatusChange(order.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-28" data-testid={`select-status-${order.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FURNISHING_ORDER_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-xs" data-testid={`text-shipping-to-${order.id}`}>
                  {addr ? (
                    <span className="text-gray-700">
                      <span className="font-medium block">{addr.label}</span>
                      <span className="text-gray-400">{addr.addressLine1}, {addr.city}</span>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    {order.orderDate && (
                      <span className="text-xs text-gray-500" data-testid={`text-order-date-${order.id}`}>
                        {new Date(order.orderDate).toLocaleDateString()}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      {order.purchaseUrl && (
                        <a href={order.purchaseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1" data-testid={`link-buy-${order.id}`}>
                          <ExternalLink className="h-3 w-3" />
                          Buy
                        </a>
                      )}
                      {order.orderUrl && (
                        <a href={order.orderUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline text-xs flex items-center gap-1" data-testid={`link-track-${order.id}`}>
                          <ArrowRight className="h-3 w-3" />
                          Track
                        </a>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Button disabled={readOnly} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(order)} data-testid={`button-edit-order-${order.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button disabled={readOnly} size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => onDelete(order.id)} data-testid={`button-delete-order-${order.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
